/**
 * `PlanLimitNotice` — shared inline notice for an `ApiError.plan_limit`
 * (ADR-0143 §8, 402 entitlements cap-check). Renders the server's
 * friendly `message`, a `count / limit dimension used` line when the
 * server tracked a live count, and an optional "View plans" CTA.
 *
 * A persistent inline card, not a toast — the 4s auto-dismiss on
 * `useToast()` doesn't give a merchant time to read an upgrade message
 * and act on it.
 *
 * Two navigation modes for the CTA, matching how deep the caller sits
 * in the view tree: `onViewPlans` for callers that already carry a
 * `setView` (e.g. `DomainStep`, one layer under the router), `viewPlansHref`
 * for callers nested too deep to drill `setView` through cleanly (e.g.
 * `RedTeamModal` inside `Guardrails`, which takes no props at all) —
 * same `/?view=...` full-page-link pattern `ShadowEscalationView` uses
 * in `Guardrails.tsx`. Omit both for no CTA.
 */

import { TrendingUp } from 'lucide-react';

import type { PlanLimitInfo } from '../../api/http-client.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface PlanLimitNoticeProps {
  planLimit: PlanLimitInfo;
  onViewPlans?: () => void;
  viewPlansHref?: string;
}

export function PlanLimitNotice({
  planLimit,
  onViewPlans,
  viewPlansHref,
}: PlanLimitNoticeProps): JSX.Element {
  const C = useC();
  return (
    <div
      className="flex items-start gap-3 p-4"
      style={{ background: C.amberDim, border: `1px solid ${C.amberFg}`, borderRadius: 4 }}
    >
      <TrendingUp size={16} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div style={{ fontFamily: sansFont, fontSize: 13, fontWeight: 600, color: C.text }}>
          {planLimit.message}
        </div>
        {planLimit.count !== undefined ? (
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              marginTop: 4,
              letterSpacing: 0.5,
            }}
          >
            {planLimit.count.toLocaleString('en-US')} / {planLimit.limit.toLocaleString('en-US')} {planLimit.dimension} used
          </div>
        ) : null}
        {onViewPlans !== undefined ? (
          <button
            type="button"
            onClick={onViewPlans}
            className="mt-3 px-3 py-2"
            style={{
              background: C.amber,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 12,
              borderRadius: 2,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            View plans
          </button>
        ) : viewPlansHref !== undefined ? (
          <a
            href={viewPlansHref}
            className="inline-block mt-3 px-3 py-2"
            style={{
              background: C.amber,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 12,
              borderRadius: 2,
              textDecoration: 'none',
            }}
          >
            View plans
          </a>
        ) : null}
      </div>
    </div>
  );
}
